/**
 * AI Configuration Management Component
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Alert,
  AlertTitle,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Science as TestIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { ApiService } from '../services/api';
import { logger } from '../utils/logger';

interface AIProvider {
  configured: boolean;
  model: string;
  status: 'ready' | 'not_configured';
}

interface AISetting {
  key: string;
  value: string | null;
  type: string;
  description: string;
  encrypted: boolean;
  category: string;
  configured: boolean;
}

interface AIStatus {
  enabled: boolean;
  ready: boolean;
  default_provider: string;
  providers: Record<string, AIProvider>;
  features: Record<string, boolean>;
  configuration_required: boolean;
  max_tokens: number;
  temperature: number;
}

export const AIConfigurationManager: React.FC = () => {
  const [settings, setSettings] = useState<AISetting[]>([]);
  const [status, setStatus] = useState<AIStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);
  const [showApiKeys, setShowApiKeys] = useState<Record<string, boolean>>({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadConfiguration();
  }, []);

  const loadConfiguration = async () => {
    try {
      setLoading(true);
      
      const response = await ApiService.get<{
        success: boolean;
        data: {
          settings: AISetting[];
          status: AIStatus;
        };
      }>('/admin/ai/config');

      setSettings(response.data.settings);
      setStatus(response.data.status);
      
      logger.info('AI configuration loaded', { 
        settingsCount: response.data.settings.length,
        ready: response.data.status.ready 
      });

    } catch (error) {
      logger.error('Failed to load AI configuration', { error });
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = (key: string, value: string) => {
    setSettings(prev => 
      prev.map(setting => 
        setting.key === key 
          ? { ...setting, value, configured: !!value }
          : setting
      )
    );
    setHasChanges(true);
  };

  const handleSaveConfiguration = async () => {
    try {
      setSaving(true);
      
      const settingsToUpdate = settings
        .filter(setting => setting.value !== '***ENCRYPTED***')
        .map(setting => ({
          key: setting.key,
          value: setting.value
        }));

      await ApiService.put('/admin/ai/config', {
        settings: settingsToUpdate
      });

      // Reload to get updated status
      await loadConfiguration();
      setHasChanges(false);
      
      logger.info('AI configuration saved successfully');

    } catch (error) {
      logger.error('Failed to save AI configuration', { error });
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (provider: string) => {
    try {
      setTesting(true);
      
      const apiKeySetting = settings.find(s => s.key === `ai.${provider}.api_key`);
      
      if (!apiKeySetting?.value || apiKeySetting.value === '***ENCRYPTED***') {
        alert(`Please configure the ${provider} API key first`);
        return;
      }

      const response = await ApiService.post<{
        success: boolean;
        data: any;
      }>('/admin/ai/test-connection', {
        provider,
        api_key: apiKeySetting.value
      });

      setTestResult({
        provider,
        ...response.data
      });
      setShowDialog(true);
      
      logger.info('AI connection test completed', { 
        provider, 
        success: response.data.success 
      });

    } catch (error) {
      logger.error('AI connection test failed', { error, provider });
      setTestResult({
        provider,
        success: false,
        error: 'Connection test failed',
        message: error instanceof Error ? error.message : 'Unknown error'
      });
      setShowDialog(true);
    } finally {
      setTesting(false);
    }
  };

  const handleInitializeDefaults = async () => {
    try {
      setLoading(true);
      
      await ApiService.post('/admin/ai/initialize');
      await loadConfiguration();
      
      logger.info('AI defaults initialized successfully');

    } catch (error) {
      logger.error('Failed to initialize AI defaults', { error });
    } finally {
      setLoading(false);
    }
  };

  const toggleApiKeyVisibility = (key: string) => {
    setShowApiKeys(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const renderProviderStatus = (provider: string, data: AIProvider) => (
    <Card key={provider} variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
            {provider}
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            <Chip 
              label={data.status === 'ready' ? 'Ready' : 'Not Configured'}
              color={data.status === 'ready' ? 'success' : 'warning'}
              icon={data.status === 'ready' ? <CheckIcon /> : <ErrorIcon />}
              size="small"
            />
            <Tooltip title={`Test ${provider} connection`}>
              <IconButton 
                onClick={() => handleTestConnection(provider)}
                disabled={!data.configured || testing}
                size="small"
              >
                <TestIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
        
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Model: {data.model}
        </Typography>
        
        <Typography variant="body2" color="text.secondary">
          Status: {data.configured ? 'API key configured' : 'API key required'}
        </Typography>
      </CardContent>
    </Card>
  );

  const renderSettingsTable = (category: string) => {
    const categorySettings = settings.filter(s => s.category === category);
    
    return (
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Setting</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categorySettings.map((setting) => (
              <TableRow key={setting.key}>
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {setting.key}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {setting.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  {setting.encrypted ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        size="small"
                        type={showApiKeys[setting.key] ? 'text' : 'password'}
                        value={setting.value || ''}
                        onChange={(e) => handleSettingChange(setting.key, e.target.value)}
                        placeholder="Enter API key..."
                        sx={{ minWidth: 200 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => toggleApiKeyVisibility(setting.key)}
                      >
                        {showApiKeys[setting.key] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </Box>
                  ) : setting.type === 'boolean' ? (
                    <Switch
                      checked={setting.value === 'true'}
                      onChange={(e) => handleSettingChange(setting.key, e.target.checked ? 'true' : 'false')}
                    />
                  ) : (
                    <TextField
                      size="small"
                      value={setting.value || ''}
                      onChange={(e) => handleSettingChange(setting.key, e.target.value)}
                      type={setting.type === 'integer' ? 'number' : 'text'}
                      sx={{ minWidth: 150 }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Chip label={setting.type} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={setting.configured ? 'Configured' : 'Not Set'}
                    color={setting.configured ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {setting.encrypted && (
                    <Tooltip title="Test this API key">
                      <IconButton
                        size="small"
                        onClick={() => {
                          const provider = setting.key.includes('claude') ? 'claude' : 'openai';
                          handleTestConnection(provider);
                        }}
                        disabled={!setting.value || setting.value === '***ENCRYPTED***'}
                      >
                        <TestIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <SettingsIcon />
            <Typography variant="h5">AI Configuration</Typography>
          </Box>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={2}>
              <SettingsIcon />
              <Typography variant="h5">AI Configuration</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadConfiguration}
                disabled={loading}
              >
                Refresh
              </Button>
              {settings.length === 0 && (
                <Button
                  variant="contained"
                  onClick={handleInitializeDefaults}
                  disabled={loading}
                >
                  Initialize Defaults
                </Button>
              )}
              {hasChanges && (
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveConfiguration}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              )}
            </Box>
          </Box>

          {status && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6">Overall Status</Typography>
                    <Chip 
                      label={status.ready ? 'Ready' : 'Configuration Required'}
                      color={status.ready ? 'success' : 'warning'}
                      icon={status.ready ? <CheckIcon /> : <ErrorIcon />}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6">Default Provider</Typography>
                    <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                      {status.default_provider}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6">Max Tokens</Typography>
                    <Typography variant="body1">{status.max_tokens}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6">Temperature</Typography>
                    <Typography variant="body1">{status.temperature}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {status?.configuration_required && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              <AlertTitle>Configuration Required</AlertTitle>
              Please configure at least one AI provider to enable SpecKit features.
            </Alert>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Tabs value={currentTab} onChange={(_, newTab) => setCurrentTab(newTab)}>
            <Tab label="Provider Status" />
            <Tab label="AI Settings" />
            <Tab label="SpecKit Settings" />
            <Tab label="Features" />
          </Tabs>

          <Box sx={{ mt: 3 }}>
            {currentTab === 0 && status && (
              <Box>
                <Typography variant="h6" gutterBottom>Provider Status</Typography>
                {Object.entries(status.providers).map(([provider, data]) => 
                  renderProviderStatus(provider, data)
                )}
              </Box>
            )}

            {currentTab === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>AI Settings</Typography>
                {renderSettingsTable('ai')}
              </Box>
            )}

            {currentTab === 2 && (
              <Box>
                <Typography variant="h6" gutterBottom>SpecKit Settings</Typography>
                {renderSettingsTable('speckit')}
              </Box>
            )}

            {currentTab === 3 && status && (
              <Box>
                <Typography variant="h6" gutterBottom>Available Features</Typography>
                <Grid container spacing={2}>
                  {Object.entries(status.features).map(([feature, enabled]) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={feature}>
                      <Card variant="outlined">
                        <CardContent>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Chip
                              label={enabled ? 'Available' : 'Disabled'}
                              color={enabled ? 'success' : 'default'}
                              size="small"
                            />
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                              {feature.replace(/_/g, ' ')}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Test Result Dialog */}
      <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Connection Test Result - {testResult?.provider}
        </DialogTitle>
        <DialogContent>
          {testResult && (
            <Box>
              <Alert 
                severity={testResult.success ? 'success' : 'error'}
                sx={{ mb: 2 }}
              >
                <AlertTitle>
                  {testResult.success ? 'Connection Successful' : 'Connection Failed'}
                </AlertTitle>
                {testResult.message || testResult.error}
              </Alert>
              
              {testResult.response_time && (
                <Typography variant="body2">
                  Response Time: {testResult.response_time}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};